import { useQuery } from '@tanstack/react-query'
import { funnelApi } from '../../api/funnel'
import { RefusedError } from '../../api/client'
import { PillarChip } from '../../components/PillarChip'
import { useSpoke } from '../../hooks/useSpoke'
import { formatCompact, formatMoney } from '../../lib/format'
import type { FunnelReport } from '../../types/funnel'

// The activity funnel (REQ-071): meetings held, proposals sent and wins over the trailing quarter, the two
// conversion rates, and what the origination goal needs a week at these averages. Every number arrived computed
// from the API; a rate with nothing under it reads as unknown, and an assumed number is said to be assumed.

const money = (v: number) => (v >= 1_000_000 ? '$' + formatCompact(v) : formatMoney(v))
const rate = (r: number | null) => (r == null ? '—' : `${Math.round(r * 100)}%`)
const one = (n: number | null) => (n == null ? '—' : n.toLocaleString(undefined, { maximumFractionDigits: 1 }))

export function FunnelBand() {
  const spoke = useSpoke()
  const q = useQuery({ queryKey: ['funnel', spoke], queryFn: () => funnelApi.view(spoke), staleTime: 60_000 })
  if (q.isPending) return <section className="glance funnel-band"><p className="muted">Measuring your activity…</p></section>
  if (q.isError) return <section className="glance funnel-band"><p className="muted">{q.error instanceof RefusedError ? q.error.refusal.message : q.error.message}</p></section>
  return <FunnelView r={q.data} />
}

function FunnelView({ r }: { r: FunnelReport }) {
  const needed = r.meetingsPerWeekNeeded
  return (
    <section className="glance funnel-band" aria-label="Your activity funnel">
      <div className="funnel-numbers">
        <p className="kicker">Your activity · last {r.weeks} weeks <PillarChip pillar={r.pillar} /></p>
        <div className="glance-stats funnel-stats">
          <Stat n={r.meetingsHeld} label={`external meetings · ${one(r.meetingsPerWeek)} a week`} />
          <Stat n={r.proposalsSent} label="proposals out" />
          <Stat n={r.won} label={r.wonAmount > 0 ? `won · ${money(r.wonAmount)}` : 'won'} />
          <Stat text={r.averageProjectSize != null ? money(r.averageProjectSize) : '—'} label="average project" title={r.averageProjectSizeBasis} />
          <Stat text={rate(r.meetingToProposal)} label="meetings → proposals" title={r.meetingToProposal == null ? 'No meeting held in the window, so the rate is unknown rather than zero.' : undefined} />
          <Stat text={rate(r.proposalToWin)} label="proposals → won" title={r.proposalToWin == null ? 'No proposal went out in the window, so the rate is unknown rather than zero.' : undefined} />
        </div>
      </div>

      <div className="funnel-goal">
        <p className="kicker">To reach {money(r.goal)} a year at your averages</p>
        {needed != null
          ? <p className="funnel-line">You need <strong>{one(needed)}</strong> external meetings a week: {one(r.projectsNeeded)} projects → {one(r.proposalsNeeded)} proposals → {one(r.meetingsNeeded)} meetings a year.</p>
          : <p className="funnel-line muted">The back-solve waits for a first won amount on file; until then there is no average project size to divide the goal by.</p>}
        {r.assumed.length > 0 && (
          <ul className="funnel-assumed" aria-label="Assumed, not measured">
            {r.assumed.map((a) => <li key={a} className="muted small">Assumed — {a}.</li>)}
          </ul>
        )}
        <dl className="funnel-levers">
          {r.levers.map((l) => (
            <div key={l.key}>
              <dt>{l.label}{l.key === 'more-meetings' && l.value != null && l.value > 0 && <span className="chip warn">+{one(l.value)} a week</span>}</dt>
              <dd className="muted small">{l.detail}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  )
}

function Stat({ n, text, label, title }: { n?: number; text?: string; label: string; title?: string }) {
  return (
    <div className="glance-stat" title={title}>
      <strong>{text ?? (n ?? 0).toLocaleString()}</strong>
      <span>{label}</span>
    </div>
  )
}
